/*
 * STEP 2 — Photo Upload
 * Design: Quiet Reverence — upload frames styled as picture frames
 * Drag-and-drop + tap-to-upload. Caption field per photo.
 * Supports JPG, PNG, HEIC (phone native format).
 */

import { ACCEPTED_PHOTO_TYPES, PhotoAsset, TIER_INFO, VideoTier, formatFileSize } from "./intake";
import { Camera, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
const nanoid = (n = 10) => Math.random().toString(36).substring(2, 2 + n);

interface Props {
  photos: PhotoAsset[];
  tier: VideoTier;
  onChange: (photos: PhotoAsset[]) => void;
}

export default function Step2Photos({ photos, tier, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const limit = TIER_INFO[tier].photoLimit;

  const addFiles = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    const remaining = limit - photos.length;
    const toAdd = arr.slice(0, remaining);
    const newPhotos: PhotoAsset[] = toAdd.map(file => ({
      id: nanoid(),
      file,
      previewUrl: URL.createObjectURL(file),
      caption: '',
    }));
    onChange([...photos, ...newPhotos]);
  }, [photos, onChange, limit]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const removePhoto = (id: string) => {
    const p = photos.find(p => p.id === id);
    if (p) URL.revokeObjectURL(p.previewUrl);
    onChange(photos.filter(p => p.id !== id));
  };

  const updateCaption = (id: string, caption: string) => {
    onChange(photos.map(p => p.id === id ? { ...p, caption } : p));
  };

  return (
    <div className="step-enter space-y-6">
      <p className="text-sm text-muted-foreground">
        Upload up to <strong className="text-foreground">{limit} photos</strong> for your {TIER_INFO[tier].label} package.
        Include a caption for each photo — these become the lower-third titles in the video.
      </p>

      {/* Upload Zone */}
      {photos.length < limit && (
        <div
          className={`upload-frame p-8 flex flex-col items-center justify-center gap-3 cursor-pointer ${dragOver ? 'drag-over' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <Camera className="w-7 h-7 text-primary" />
          </div>
          <div className="text-center">
            <p className="font-['Playfair_Display'] font-600 text-foreground text-lg">Add Photos</p>
            <p className="text-sm text-muted-foreground mt-1">
              Tap to browse or drag photos here
            </p>
            <p className="text-xs text-muted-foreground mt-1">JPG, PNG, HEIC — any size</p>
          </div>
          <input
            ref={inputRef}
            id="photo-file-input"
            name="photo_files"
            type="file"
            accept={ACCEPTED_PHOTO_TYPES}
            multiple
            capture="environment"
            className="hidden"
            onChange={e => e.target.files && addFiles(e.target.files)}
          />
        </div>
      )}

      {/* Photo count indicator */}
      {photos.length > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{photos.length} of {limit} photos added</span>
          <div className="progress-line w-32">
            <div className="progress-line-fill" style={{ width: `${(photos.length / limit) * 100}%` }} />
          </div>
        </div>
      )}

      {/* Photo Grid */}
      {photos.length > 0 && (
        <div className="space-y-4">
          {photos.map((photo) => (
            <div key={photo.id} className="thumb-enter flex gap-3 p-3 bg-card rounded-xl border border-border">
              {/* Thumbnail */}
              <div className="relative flex-shrink-0">
                <img
                  src={photo.previewUrl}
                  alt="Preview"
                  className="w-20 h-20 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(photo.id)}
                  className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-sm hover:opacity-90 transition"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              {/* Caption */}
              <div className="flex-1 space-y-1.5">
                <div className="text-xs text-muted-foreground truncate">{photo.file ? `${photo.file.name} · ${formatFileSize(photo.file.size)}` : photo.r2Key ? '✓ Uploaded to cloud' : 'File ready'}</div>
                <input
                  id={`photo-caption-${photo.id}`}
                  name={`photo_caption_${photo.id}`}
                  type="text"
                  value={photo.caption}
                  onChange={e => updateCaption(photo.id, e.target.value)}
                  placeholder='e.g. "Bill and Martha Wedding 1945"'
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition"
                />
                <p className="text-xs text-muted-foreground">Caption appears as a title card in the video</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {photos.length === 0 && (
        <div className="text-center py-4 text-sm text-muted-foreground font-['Playfair_Display'] italic">
          No photos added yet — every photo tells part of the story.
        </div>
      )}
    </div>
  );
}
